const ALLOWED_CHARS = /^[\d.\s+\-*/()]+$/;

const roundToDecimalPlaces = (value, decimalPlaces) => {
  const factor = 10 ** decimalPlaces;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

const normalizeExpression = raw => {
  if (raw == null) return '';
  return String(raw).trim().replace(/^=/, '').trim();
};

export const isArithmeticExpression = raw => {
  const trimmed = String(raw).trim();
  if (/^=/.test(trimmed)) return true;

  const normalized = normalizeExpression(raw);
  if (!normalized) return false;
  if (/[+*/()]/.test(normalized)) return true;
  return /(?<=[\d.)])\s*-/.test(normalized);
};

export const evaluateArithmeticExpression = (raw, decimalPlaces = 2) => {
  const expression = normalizeExpression(raw);
  if (!expression) {
    return { ok: false };
  }

  if (!ALLOWED_CHARS.test(expression)) {
    return { ok: false };
  }

  let index = 0;

  const peek = () => expression[index];
  const consume = () => expression[index++];

  const skipWhitespace = () => {
    while (/\s/.test(peek())) {
      index += 1;
    }
  };

  const parseNumber = () => {
    skipWhitespace();
    const start = index;
    let sawDigit = false;
    let sawDot = false;

    while (index < expression.length) {
      const char = expression[index];
      if (char >= '0' && char <= '9') {
        sawDigit = true;
        index += 1;
      } else if (char === '.' && !sawDot) {
        sawDot = true;
        index += 1;
      } else {
        break;
      }
    }

    if (!sawDigit) {
      return null;
    }

    const value = Number(expression.slice(start, index));
    return Number.isFinite(value) ? value : null;
  };

  const parseFactor = () => {
    skipWhitespace();
    const char = peek();

    if (char === '-') {
      consume();
      const value = parseFactor();
      if (value == null) return null;
      return -value;
    }

    if (char === '+') {
      consume();
      return parseFactor();
    }

    if (char === '(') {
      consume();
      const value = parseExpression();
      skipWhitespace();
      if (peek() !== ')') return null;
      consume();
      return value;
    }

    return parseNumber();
  };

  const parseTerm = () => {
    let value = parseFactor();
    if (value == null) return null;

    while (true) {
      skipWhitespace();
      const operator = peek();
      if (operator !== '*' && operator !== '/') break;
      consume();
      const right = parseFactor();
      if (right == null) return null;
      if (operator === '*') {
        value *= right;
      } else if (right === 0) {
        return null;
      } else {
        value /= right;
      }
    }

    return value;
  };

  const parseExpression = () => {
    let value = parseTerm();
    if (value == null) return null;

    while (true) {
      skipWhitespace();
      const operator = peek();
      if (operator !== '+' && operator !== '-') break;
      consume();
      const right = parseTerm();
      if (right == null) return null;
      value = operator === '+' ? value + right : value - right;
    }

    return value;
  };

  try {
    const result = parseExpression();
    skipWhitespace();
    if (result == null || index !== expression.length || !Number.isFinite(result)) {
      return { ok: false };
    }

    return {
      ok: true,
      value: roundToDecimalPlaces(result, decimalPlaces)
    };
  } catch {
    return { ok: false };
  }
};
